"use client";

import { Consultation } from "@/app/_components/consultation-card";
import { Error } from "@/app/_components/error";
import { Loading } from "../loading";
import { SearchLink } from "../search-link";
import { ExOwnerInfo } from "../ex-owner-info";

import { useAnalyze } from "@/queries/use-analyze";

import type { DiligenceItem } from "../diligence.types";

const urls = {
  deskdata: {
    url: "https://deskdata.com.br/",
    label: "deskdata.com.br",
    icon: "/logos/deskdata-logo.svg",
  },
  google: {
    url: "https://www.google.com/search?q={query}",
    label: "Google",
    icon: "/logos/google-logo.svg",
  },
  receita: {
    url: "https://servicos.receita.fazenda.gov.br/servicos/cpf/consultasituacao/consultapublica.asp",
    label: "Consultar CPF na Receita",
    icon: "/logos/receita-federal-logo.svg",
  },
  certidaoFederal: {
    url: "https://solucoes.receita.fazenda.gov.br/Servicos/certidaointernet/PF/Emitir",
    label: "Emitir Certidão Federal",
    icon: "/logos/receita-federal-logo.svg",
  },
  cadin: {
    url: "https://www.gov.br/pt-br/servicos/consultar-inclusao-no-cadin-sisbacen-pela-receita-federal",
    label: "Consultar CADIN",
    icon: "/logos/govbr-logo.svg",
  },
};

const createSearchUrl = (baseUrl: string, query: string) => {
  return baseUrl.replace("{query}", encodeURIComponent(query || ""));
};

export const Propietarios = ({ id, field, emptyElement }: DiligenceItem) => {
  const { data, isPending, error, refetch } = useAnalyze({ id, field });

  if (isPending) return <Loading />;

  if (error) return <Error reset={refetch} />;

  const analyzeData = data?.analise?.data;
  const property = data?.propriedade;
  const hasValidData = analyzeData && property;

  if (!hasValidData) {
    return emptyElement ?? <p>Não há dados válidos para exibir.</p>;
  }

  const uf = property.uf || "";
  const city = property.cidade_descricao || "";
  const inscricaoImobiliaria = property?.inscricao_imobiliaria || "";

  const certidaoEstadualSearchUrl = createSearchUrl(
    urls.google.url,
    `certidão negativa estadual ${uf} pessoa física`
  );
  const certidaoMunicipalSearchUrl = createSearchUrl(
    urls.google.url,
    `certidão negativa municipal ${city} ${uf} pessoa física`
  );

  const currentOwner =
    analyzeData.moradores && analyzeData.moradores.length > 0
      ? analyzeData.moradores[0]
      : null;

  return (
    <div className="flex flex-col gap-8">
      <ExOwnerInfo
        currentOwner={currentOwner}
        analyzeData={analyzeData}
        propertyNumber={inscricaoImobiliaria}
      />
      <Consultation.Card
        title="Informações Gerais"
        description="Seja para tentar obter informações privilegiadas antes do leilão, seja para notificar extrajudicialmente após a arrematação ― ter as informações do ex-proprietário pode ser útil."
      >
        <p className="text-sm text-muted-foreground">
          Para isso, realize uma consulta em um site agregador por meio do CPF.
        </p>
        <SearchLink
          url={urls.deskdata.url}
          label={urls.deskdata.label}
          icon={urls.deskdata.icon}
        />
        <p className="text-sm text-muted-foreground">
          Com o resultado em mãos, pode-se encontrar dados como nome da mãe,
          endereços, telefones, histórico profissional, participações em
          empresas, etc.
        </p>
      </Consultation.Card>
      <Consultation.Card
        title="Consulta de CPF"
        description="Verifique a regularidade cadastral do CPF junto à Receita Federal."
      >
        <SearchLink
          url={urls.receita.url}
          label={urls.receita.label}
          icon={urls.receita.icon}
        />
      </Consultation.Card>
      <Consultation.Card
        title="Consulta ao CADIN"
        description="Verifique se o CPF está incluído no Cadastro Informativo de Créditos não Quitados. (Cadin) é um banco de dados onde estão registrados os nomes de pessoas em débito com órgãos e entidades federais."
      >
        <SearchLink
          url={urls.cadin.url}
          label={urls.cadin.label}
          icon={urls.cadin.icon}
        />
      </Consultation.Card>
      <Consultation.Card
        title="Certidões Estaduais e Municipais"
        description="Se o ITR se aplica ao imóvel, você pode emitir a certidão negativa (CND Federal)."
      >
        <SearchLink
          url={urls.certidaoFederal.url}
          label={urls.certidaoFederal.label}
          icon={urls.certidaoFederal.icon}
        />
        <p className="text-sm text-muted-foreground">
          {" "}
          Pesquise certidões negativas estaduais e municipais do{" "}
          <span className="uppercase">{uf}</span> e {city}.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <SearchLink
            url={certidaoEstadualSearchUrl}
            label="Consultar Certidões Estaduais"
            icon={urls.google.icon}
          />
          <SearchLink
            url={certidaoMunicipalSearchUrl}
            label="Consultar Certidões Municipais"
            icon={urls.google.icon}
          />
        </div>
      </Consultation.Card>
    </div>
  );
};
